import { Component, computed, input } from '@angular/core';
import { ContactHoursComponent } from '../../../../../../shared/components/contact-hours/contact-hours.component';
import { TelephoneLinkComponent } from '../../../../../../shared/components/telephone-link/telephone-link.component';
import { capitaliseText, formatAddress } from '../../../../../../shared/utils/formatting';
import { MmrService } from '../../../../services/mmr.service';
import { ClaimDetailsPanelViewClaim } from '../../../../types/mmr-claims.type';
import { claimsTrackerFeedbackConfig } from '../../claims-details-feedback.config';
import { RepairTrackerTimelineComponent } from './components/repair-tracker-timeline/repair-tracker-timeline.component';

@Component({
  selector: 'app-view-claim',
  imports: [TelephoneLinkComponent, ContactHoursComponent, RepairTrackerTimelineComponent],
  templateUrl: './view-claim.component.html',
  styleUrl: './view-claim.component.scss',
})
export class ViewClaimComponent {
  claimType = input.required<'repair' | 'service'>();

  feedbackConfig = claimsTrackerFeedbackConfig;

  protected viewModel = computed(() => {
    const { claims } = this.mmrService.planClaimsDetails;

    const addressInformation = claims.value()?.addressInformation;
    const isFloorCareProduct = claims.value()?.planOverview?.isFloorCareProduct ?? false;
    const isGadget = claims.value()?.planOverview?.isGadget ?? false;
    const isProductFromGame = claims.value()?.planOverview?.isProductFromGame ?? false;

    const claim = claims.value()?.[this.claimType()] as ClaimDetailsPanelViewClaim;

    if (addressInformation) {
      delete addressInformation['countryCode'];
    }

    const contactSection = this.getContactSection(
      claim?.serviceOptionType,
      isFloorCareProduct,
      isGadget,
      isProductFromGame,
      claim?.serviceProvider?.name ?? '',
      this.claimType()
    );

    return {
      postCode: addressInformation?.postalCode,
      claimType: this.claimType(),
      address: formatAddress(Object.values(addressInformation ?? '{}')),
      addressHeading: this.getAddressHeading(claim?.serviceOptionType ?? '', isProductFromGame),
      claim,
      lastUpdated: claims.value()?.lastUpdated?.toTimeString(),
      contactHours: Object.entries(claim.serviceProvider.openingHours).map(([key, value]) => ({
        day: capitaliseText(key)!,
        open: value.openingTime,
        close: value.closingTime,
      })),
      contactSection,
    };
  });

  private getContactSection(
    serviceOptionType: string | undefined,
    isFloorCareProduct: boolean,
    isGadget: boolean,
    isProductFromGame: boolean,
    providerName: string,
    claimType: 'repair' | 'service'
  ): { heading: string; body: string; body2?: string } {
    if (isFloorCareProduct) {
      return {
        heading: 'Need to change or cancel?',
        body: 'If you need to change or cancel your booking, please call our repairer.',
      };
    }
    if (isProductFromGame && serviceOptionType === 'Courier') {
      return {
        heading: 'Need to change or cancel?',
        body: 'If you need to change your collection date, please contact DPD.',
        body2: 'If you have any questions about your booking please call our provider.',
      };
    }
    if (serviceOptionType === 'Courier' && isGadget) {
      return {
        heading: 'Need to change or cancel?',
        body: 'If you need to change your collection date, please contact DPD.',
        body2: 'To change your booking details or for updates on the repair, please call our repairer.',
      };
    }
    if (serviceOptionType === 'Self Send' && isGadget) {
      return {
        heading: 'Need to change or cancel?',
        body: 'To cancel, change your details or ask for updates, please call our repairer.',
      };
    }
    if (serviceOptionType === 'Self Fit') {
      return {
        heading: 'Need an update?',
        body: 'Please call the part supplier if you have any questions or concerns.',
      };
    }
    return {
      heading: 'Need an update?',
      body: `Please call ${providerName} if you have any questions or concerns about the ${claimType}.`,
    };
  }

  private getAddressHeading(serviceOptionType: string, isProductFromGame: boolean): string {
    if ((serviceOptionType ?? '').toLowerCase() === 'self send') {
      return 'Return delivery address';
    }

    if ((serviceOptionType ?? '').toLowerCase() === 'courier') {
      // GAME collect-and-replace claims only use the address for collection.
      return isProductFromGame ? 'Collection address' : 'Collection and return address';
    }
    return 'Address';
  }

  get vm() {
    return this.viewModel();
  }

  constructor(private readonly mmrService: MmrService) {}
}
